import { getStroke } from 'perfect-freehand';

import { Camera, CanvasElement, Point } from '@/features/canvas/types';

/** The 8 resize handles + a rotate handle. */
export type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'rotate';

export const MIN_SIZE = 16;

export const screenToWorld = (cam: Camera, sx: number, sy: number): Point => ({
  x: (sx - cam.x) / cam.zoom,
  y: (sy - cam.y) / cam.zoom,
});

export const worldToScreen = (cam: Camera, wx: number, wy: number): Point => ({
  x: wx * cam.zoom + cam.x,
  y: wy * cam.zoom + cam.y,
});

export const elementCenter = (el: { x: number; y: number; w: number; h: number }): Point => ({
  x: el.x + el.w / 2,
  y: el.y + el.h / 2,
});

/** Axis-aligned bounding box of a set of elements (connectors/freehand included via their x/y/w/h). */
export function boundsOf(els: CanvasElement[]): { x: number; y: number; w: number; h: number } | null {
  if (els.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const el of els) {
    minX = Math.min(minX, el.x);
    minY = Math.min(minY, el.y);
    maxX = Math.max(maxX, el.x + el.w);
    maxY = Math.max(maxY, el.y + el.h);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** Whether a world point is inside an element's (unrotated) box. */
export const hitTest = (el: CanvasElement, p: Point): boolean =>
  p.x >= el.x && p.x <= el.x + el.w && p.y >= el.y && p.y <= el.y + el.h;

/** hitTest with a world-space margin around the box (used for hover, so edge connection nubs — which
 *  sit just outside the border — stay "hovered" as the pointer approaches them). */
export const hitTestMargin = (el: CanvasElement, p: Point, m: number): boolean =>
  p.x >= el.x - m && p.x <= el.x + el.w + m && p.y >= el.y - m && p.y <= el.y + el.h + m;

/** Whether a world point is inside the box but within `m` of its border — the "grab the edge to draw a
 *  connector" zone (Miro). The interior beyond `m` is the move zone. */
export const nearBorder = (el: CanvasElement, p: Point, m: number): boolean => {
  if (!hitTest(el, p)) return false;
  const d = Math.min(p.x - el.x, el.x + el.w - p.x, p.y - el.y, el.y + el.h - p.y);
  return d <= m;
};

/** Whether an element's box is fully inside a world rect (for marquee selection). */
export const rectContains = (
  r: { x: number; y: number; w: number; h: number },
  el: CanvasElement,
): boolean => {
  // Unrotated: plain axis-aligned containment.
  if (!el.rotation) return el.x >= r.x && el.y >= r.y && el.x + el.w <= r.x + r.w && el.y + el.h <= r.y + r.h;
  // Rotated: the element's real footprint is its ROTATED box, so test its four rotated corners (about the
  // centre) — a marquee must enclose all of them, not the larger unrotated bounding box.
  const cx = el.x + el.w / 2;
  const cy = el.y + el.h / 2;
  const a = (el.rotation * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const hw = el.w / 2;
  const hh = el.h / 2;
  const rx1 = r.x + r.w;
  const ry1 = r.y + r.h;
  for (const [lx, ly] of [
    [-hw, -hh],
    [hw, -hh],
    [hw, hh],
    [-hw, hh],
  ]) {
    const wx = cx + lx * cos - ly * sin;
    const wy = cy + lx * sin + ly * cos;
    if (wx < r.x || wy < r.y || wx > rx1 || wy > ry1) return false;
  }
  return true;
};

/** Normalise a rect that may have negative width/height (drag up-left). */
export function normRect(x0: number, y0: number, x1: number, y1: number) {
  return { x: Math.min(x0, x1), y: Math.min(y0, y1), w: Math.abs(x1 - x0), h: Math.abs(y1 - y0) };
}

// ------------------------------------------------------------------ alignment snapping

type Rect = { x: number; y: number; w: number; h: number };

/** A world-space alignment guide: a vertical line (axis 'x') at x=`at` spanning y∈[from,to], or a
 *  horizontal line (axis 'y') at y=`at` spanning x∈[from,to]. */
export interface SnapLine {
  axis: 'x' | 'y';
  at: number;
  from: number;
  to: number;
}

const rectXLines = (r: Rect) => [r.x, r.x + r.w / 2, r.x + r.w];
const rectYLines = (r: Rect) => [r.y, r.y + r.h / 2, r.y + r.h];

/**
 * Soft-snap a moving bounding box to the OTHER boxes' edges + centres (Miro-style alignment). Each axis
 * snaps independently to the nearest candidate line within `threshold` (world px). Returns the correction
 * (dx,dy) to add to the drag plus the guide lines to draw — one per snapped axis, spanning the moving box
 * and every other box that shares the snapped line, so aligned elements visibly connect.
 */
export function computeSnap(
  moving: Rect,
  others: Rect[],
  threshold: number,
): { dx: number; dy: number; guides: SnapLine[] } {
  const mX = rectXLines(moving);
  const mY = rectYLines(moving);
  let bx: { d: number; delta: number; at: number } | null = null;
  let by: { d: number; delta: number; at: number } | null = null;
  for (const o of others) {
    for (const ol of rectXLines(o))
      for (const ml of mX) {
        const d = Math.abs(ml - ol);
        if (d <= threshold && (!bx || d < bx.d)) bx = { d, delta: ol - ml, at: ol };
      }
    for (const ol of rectYLines(o))
      for (const ml of mY) {
        const d = Math.abs(ml - ol);
        if (d <= threshold && (!by || d < by.d)) by = { d, delta: ol - ml, at: ol };
      }
  }
  const dx = bx ? bx.delta : 0;
  const dy = by ? by.delta : 0;
  const snapped: Rect = { x: moving.x + dx, y: moving.y + dy, w: moving.w, h: moving.h };
  const guides: SnapLine[] = [];
  if (bx) {
    // Span the vertical guide across the moving box + every other box that also lies on this line.
    let from = snapped.y;
    let to = snapped.y + snapped.h;
    for (const o of others)
      if (rectXLines(o).some((l) => Math.abs(l - bx!.at) < 0.5)) {
        from = Math.min(from, o.y);
        to = Math.max(to, o.y + o.h);
      }
    guides.push({ axis: 'x', at: bx.at, from, to });
  }
  if (by) {
    let from = snapped.x;
    let to = snapped.x + snapped.w;
    for (const o of others)
      if (rectYLines(o).some((l) => Math.abs(l - by!.at) < 0.5)) {
        from = Math.min(from, o.x);
        to = Math.max(to, o.x + o.w);
      }
    guides.push({ axis: 'y', at: by.at, from, to });
  }
  return { dx, dy, guides };
}

/** Snap a single scalar (a connector rail's coordinate) to the nearest candidate within `threshold`. */
export function snap1D(v: number, candidates: number[], threshold: number): number {
  let best = v;
  let bestD = threshold;
  for (const c of candidates) {
    const d = Math.abs(v - c);
    if (d <= bestD) {
      bestD = d;
      best = c;
    }
  }
  return best;
}

/** Snap a connector rail's coordinate (`axis` = which coordinate — 'x' for a vertical rail, 'y' for a
 *  horizontal one) to the OTHER boxes' matching edge/centre lines, returning the snapped value plus a
 *  guide spanning the aligned boxes (or null when nothing is within `threshold`). */
export function snapRail(
  axis: 'x' | 'y',
  v: number,
  others: Rect[],
  threshold: number,
): { v: number; guide: SnapLine | null } {
  const lineFn = axis === 'x' ? rectXLines : rectYLines;
  let best: { d: number; at: number } | null = null;
  for (const o of others)
    for (const l of lineFn(o)) {
      const d = Math.abs(v - l);
      if (d <= threshold && (!best || d < best.d)) best = { d, at: l };
    }
  if (!best) return { v, guide: null };
  let from = Infinity;
  let to = -Infinity;
  for (const o of others)
    if (lineFn(o).some((l) => Math.abs(l - best!.at) < 0.5)) {
      if (axis === 'x') {
        from = Math.min(from, o.y);
        to = Math.max(to, o.y + o.h);
      } else {
        from = Math.min(from, o.x);
        to = Math.max(to, o.x + o.w);
      }
    }
  return { v: best.at, guide: { axis, at: best.at, from, to } };
}

/**
 * New geometry for a resize drag. Rotation is intentionally ignored in the math (handles are still
 * positioned on the rotated frame) — correct for the common rotation===0 case, and acceptable for the
 * rare rotated resize. The opposite edge/corner stays put; MIN_SIZE floors width/height.
 */
export function resizeRect(
  el: { x: number; y: number; w: number; h: number },
  handle: Handle,
  p: Point,
): { x: number; y: number; w: number; h: number } {
  let { x, y, w, h } = el;
  const right = el.x + el.w;
  const bottom = el.y + el.h;
  if (handle.includes('w')) {
    x = Math.min(p.x, right - MIN_SIZE);
    w = right - x;
  }
  if (handle.includes('e')) {
    w = Math.max(MIN_SIZE, p.x - el.x);
  }
  if (handle.includes('n')) {
    y = Math.min(p.y, bottom - MIN_SIZE);
    h = bottom - y;
  }
  if (handle.includes('s')) {
    h = Math.max(MIN_SIZE, p.y - el.y);
  }
  return { x, y, w, h };
}

/** Angle (radians) from an element's centre to a world point — for the rotate handle. */
export const angleTo = (center: Point, p: Point): number => Math.atan2(p.y - center.y, p.x - center.x);

/**
 * Endpoints of a connector between two boxes: the segment joining their centres, clipped to each
 * box's border so the line touches edges rather than centres. Returns world coords.
 */
export function connectorLine(
  from: { x: number; y: number; w: number; h: number },
  to: { x: number; y: number; w: number; h: number },
): { x1: number; y1: number; x2: number; y2: number } {
  const c1 = elementCenter(from);
  const c2 = elementCenter(to);
  return {
    ...borderPoint(from, c1, c2),
    ...borderPoint2(to, c2, c1),
  };
}

/** Where the ray from `center` toward `toward` exits box `b` (its border). */
function borderPoint(b: { x: number; y: number; w: number; h: number }, center: Point, toward: Point) {
  const p = rayBoxExit(b, center, toward);
  return { x1: p.x, y1: p.y };
}
function borderPoint2(b: { x: number; y: number; w: number; h: number }, center: Point, toward: Point) {
  const p = rayBoxExit(b, center, toward);
  return { x2: p.x, y2: p.y };
}

export function rayBoxExit(b: { x: number; y: number; w: number; h: number }, center: Point, toward: Point): Point {
  const dx = toward.x - center.x;
  const dy = toward.y - center.y;
  if (dx === 0 && dy === 0) return center;
  const hw = b.w / 2;
  const hh = b.h / 2;
  // Scale the direction so it just reaches the box border (whichever axis is limiting).
  const scaleX = dx !== 0 ? hw / Math.abs(dx) : Infinity;
  const scaleY = dy !== 0 ? hh / Math.abs(dy) : Infinity;
  const s = Math.min(scaleX, scaleY);
  return { x: center.x + dx * s, y: center.y + dy * s };
}

// ---- pressure-aware ink (pen) ----

const avg = (a: number, b: number) => (a + b) / 2;

/** Turn a perfect-freehand outline polygon into a smooth, closed SVG fill path (their median-point form). */
function outlineToPath(pts: number[][]): string {
  const len = pts.length;
  if (len < 4) {
    if (!len) return '';
    const [x, y] = pts[0];
    return `M ${x.toFixed(2)} ${y.toFixed(2)} m -1 0 a 1 1 0 1 0 2 0 a 1 1 0 1 0 -2 0`;
  }
  let a = pts[0];
  let b = pts[1];
  const c = pts[2];
  let d = `M ${a[0].toFixed(2)} ${a[1].toFixed(2)} Q ${b[0].toFixed(2)} ${b[1].toFixed(2)} ${avg(b[0], c[0]).toFixed(2)} ${avg(b[1], c[1]).toFixed(2)} T`;
  for (let i = 2; i < len - 1; i++) {
    a = pts[i];
    b = pts[i + 1];
    d += ` ${avg(a[0], b[0]).toFixed(2)} ${avg(a[1], b[1]).toFixed(2)}`;
  }
  d += ' Z';
  return d;
}

const inkOptions = (size: number, realPressure: boolean) => ({
  size,
  thinning: 0.75, // wide pressure→width range for expressive strokes (points are densified upstream)
  smoothing: 0.6,
  streamline: 0.5,
  simulatePressure: !realPressure,
  last: true,
});

/** Variable-width ink OUTLINE polygon (perfect-freehand) for world points [x,y,pressure]. This is the
 *  raw geometry — the GPU (Pixi) layer fills it directly; the SVG path builder wraps it. */
export function inkOutline(points: number[][], size: number, realPressure: boolean): number[][] {
  if (!points || points.length === 0) return [];
  return getStroke(points, inkOptions(size, realPressure)) as number[][];
}

/** Ink outline as an SVG fill path (used by the draft preview + any remaining SVG ink). */
export function inkPath(points: number[][], size: number, realPressure: boolean): string {
  return outlineToPath(inkOutline(points, size, realPressure));
}

/** Ink outline (world coords) for a freehand element whose `stroke` points are stored relative to its origin. */
export function freehandOutline(el: { x: number; y: number }, stroke: number[][], size: number, realPressure: boolean): number[][] {
  return inkOutline(stroke.map((p) => [el.x + p[0], el.y + p[1], p[2] ?? 0.5]), size, realPressure);
}

/** Ink outline for a freehand element as an SVG fill path. */
export function freehandStrokePath(el: { x: number; y: number }, stroke: number[][], size: number, realPressure: boolean): string {
  return outlineToPath(freehandOutline(el, stroke, size, realPressure));
}

/** Squared distance from point (px,py) to segment (ax,ay)-(bx,by). */
function distToSeg2(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return (px - cx) * (px - cx) + (py - cy) * (py - cy);
}

/** Hit-test a freehand element by proximity to its stroke centerline (the Pixi ink layer is pointer-
 *  transparent, so clicks are resolved here instead of via SVG hit paths). `margin` is world-space slop. */
export function freehandHit(el: CanvasElement, p: Point, margin: number): boolean {
  const stroke = el.style?.stroke;
  const half = (el.style?.strokeWidth ?? 8) / 2 + margin;
  if (p.x < el.x - half || p.y < el.y - half || p.x > el.x + el.w + half || p.y > el.y + el.h + half) return false;
  if (stroke && stroke.length) {
    const h2 = half * half;
    let ax = el.x + stroke[0][0];
    let ay = el.y + stroke[0][1];
    for (let i = 1; i < stroke.length; i++) {
      const bx = el.x + stroke[i][0];
      const by = el.y + stroke[i][1];
      if (distToSeg2(p.x, p.y, ax, ay, bx, by) <= h2) return true;
      ax = bx;
      ay = by;
    }
    return false;
  }
  // legacy flat polyline
  const pts = el.style?.points;
  if (pts && pts.length >= 4) {
    const h2 = half * half;
    let ax = el.x + pts[0];
    let ay = el.y + pts[1];
    for (let i = 2; i < pts.length; i += 2) {
      const bx = el.x + pts[i];
      const by = el.y + pts[i + 1];
      if (distToSeg2(p.x, p.y, ax, ay, bx, by) <= h2) return true;
      ax = bx;
      ay = by;
    }
  }
  return false;
}

/** SVG path for a freehand stroke whose points are stored relative to the element origin. */
export function freehandPath(el: CanvasElement, points: number[]): string {
  if (!points || points.length < 2) return '';
  let d = `M ${el.x + points[0]} ${el.y + points[1]}`;
  for (let i = 2; i < points.length; i += 2) {
    d += ` L ${el.x + points[i]} ${el.y + points[i + 1]}`;
  }
  return d;
}

// ------------------------------------------------------------------ resize (grow, don't reflow)

export type ResizeMode = 'free' | 'aspect' | 'square';

/**
 * New geometry for a resize that GROWS the element (Miro-style): 'aspect' locks the original ratio,
 * 'square' locks to 1:1 (stickies), both anchored on the opposite corner; 'free' is a single-axis
 * edge drag (the old reflow behaviour). The proportional scale (newW/prevW) is returned so the caller
 * can grow the font with the box.
 */
export function computeResize(
  prev: { x: number; y: number; w: number; h: number },
  handle: Handle,
  p: Point,
  mode: ResizeMode,
  fromCenter = false,
): { x: number; y: number; w: number; h: number; scale: number } {
  const cx = prev.x + prev.w / 2;
  const cy = prev.y + prev.h / 2;
  if (mode === 'free' || handle === 'rotate') {
    if (fromCenter) {
      // Grow symmetrically about the centre: each dragged edge moves the box's half-extent to the pointer.
      const w = handle.includes('e') || handle.includes('w') ? Math.max(MIN_SIZE, 2 * Math.abs(p.x - cx)) : prev.w;
      const h = handle.includes('n') || handle.includes('s') ? Math.max(MIN_SIZE, 2 * Math.abs(p.y - cy)) : prev.h;
      return { x: cx - w / 2, y: cy - h / 2, w, h, scale: w / prev.w };
    }
    const r = resizeRect(prev, handle, p);
    return { ...r, scale: r.w / prev.w };
  }
  const aspect = mode === 'square' ? 1 : prev.w / prev.h;
  // Anchor = the corner opposite the drag (or the CENTRE with Alt); the box grows toward the pointer.
  const ax = fromCenter ? cx : handle.includes('w') ? prev.x + prev.w : prev.x;
  const ay = fromCenter ? cy : handle.includes('n') ? prev.y + prev.h : prev.y;
  let w = Math.abs(p.x - ax) * (fromCenter ? 2 : 1);
  let h = Math.abs(p.y - ay) * (fromCenter ? 2 : 1);
  if (w / aspect > h) h = w / aspect;
  else w = h * aspect;
  w = Math.max(MIN_SIZE, w);
  h = Math.max(MIN_SIZE, w / aspect);
  if (fromCenter) return { x: cx - w / 2, y: cy - h / 2, w, h, scale: w / prev.w };
  const x = handle.includes('w') ? ax - w : ax;
  const y = handle.includes('n') ? ay - h : ay;
  return { x, y, w, h, scale: w / prev.w };
}

/**
 * Resize a ROTATED element by dragging `handle` to world point `p`, working in the element's LOCAL
 * (unrotated) frame so the box grows along its own axes and the opposite corner/edge stays pinned in
 * world space — the transform controls then track the rotation instead of resizing along world axes.
 * `x,y` in the result is still the unrotated top-left (the element rotates around its centre), so the
 * caller applies it unchanged. Falls back to `computeResize` for an unrotated element.
 */
export function resizeRotated(
  prev: { x: number; y: number; w: number; h: number; rotation: number },
  handle: Handle,
  p: Point,
  mode: ResizeMode,
  fromCenter = false,
): { x: number; y: number; w: number; h: number; scale: number } {
  const deg = prev.rotation ?? 0;
  if (deg === 0 || handle === 'rotate') return computeResize(prev, handle, p, mode, fromCenter);
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const u = { x: cos, y: sin }; // element's local +x axis, in world
  const v = { x: -sin, y: cos }; // local +y axis
  const dot = (a: Point, b: Point) => a.x * b.x + a.y * b.y;
  const toLocal = (q: Point) => ({ x: dot(q, u), y: dot(q, v) }); // world → local coords (u,v orthonormal)
  const center = { x: prev.x + prev.w / 2, y: prev.y + prev.h / 2 };
  const cL = toLocal(center);
  const hw = prev.w / 2;
  const hh = prev.h / 2;
  const sx = handle.includes('e') ? 1 : handle.includes('w') ? -1 : 0; // dragged corner/edge sign (local)
  const sy = handle.includes('s') ? 1 : handle.includes('n') ? -1 : 0;
  const pL = toLocal(p);
  // With Alt the CENTRE is pinned (symmetric grow to twice the centre→pointer half-extent); otherwise the
  // opposite edge/corner is pinned.
  const anchorLx = fromCenter ? cL.x : cL.x - sx * hw;
  const anchorLy = fromCenter ? cL.y : cL.y - sy * hh;
  let w = prev.w;
  let h = prev.h;
  if (sx !== 0) w = Math.max(MIN_SIZE, (pL.x - anchorLx) * sx * (fromCenter ? 2 : 1));
  if (sy !== 0) h = Math.max(MIN_SIZE, (pL.y - anchorLy) * sy * (fromCenter ? 2 : 1));
  if (sx !== 0 && sy !== 0) {
    if (mode === 'square') {
      const m = Math.max(w, h);
      w = m;
      h = m;
    } else if (mode === 'aspect') {
      const aspect = prev.w / prev.h;
      if (w / aspect > h) h = w / aspect;
      else w = h * aspect;
      w = Math.max(MIN_SIZE, w);
      h = Math.max(MIN_SIZE, w / aspect);
    }
  }
  // New centre in local coords: fromCenter keeps cL fixed; else the anchor stays pinned along changed axes.
  const newCL = fromCenter ? cL : { x: sx !== 0 ? anchorLx + (sx * w) / 2 : cL.x, y: sy !== 0 ? anchorLy + (sy * h) / 2 : cL.y };
  const wc = { x: newCL.x * u.x + newCL.y * v.x, y: newCL.x * u.y + newCL.y * v.y }; // local → world centre
  return { x: wc.x - w / 2, y: wc.y - h / 2, w, h, scale: w / prev.w };
}

// ------------------------------------------------------------------ connector routing

type Box = { x: number; y: number; w: number; h: number };

/** A draggable segment-midpoint handle. `orientation`:
 *   - 'railh'/'railv' — the auto-routed elbow's single middle segment; dragging MOVES it (no new anchor),
 *     storing its position in `style.elbowMid`. 'railh' is a horizontal rail (drag it vertically),
 *     'railv' a vertical rail (drag it horizontally).
 *   - 'h'/'v' — a legacy elbow segment defined by explicit waypoints; dragging splices a waypoint at
 *     `controlIndex` (the older behaviour, kept for connectors that already have waypoints).
 *   - 'free' — straight/curved; dragging drops/moves the single bend at the cursor. */
export interface ConnectorHandle {
  mid: Point;
  controlIndex: number;
  orientation: 'h' | 'v' | 'free' | 'railh' | 'railv';
}

export interface ConnectorGeom {
  d: string; // SVG path
  start: Point;
  end: Point;
  startFrom: Point; // point defining the outgoing direction at the start (for a start arrowhead)
  endFrom: Point; // point defining the incoming direction at the end (for the end arrowhead)
  waypoints: { x: number; y: number }[]; // user bend points (world), for handles
  handles: ConnectorHandle[]; // segment-midpoint drag handles (one per drawn segment)
  labelAnchor: Point; // where a connector's text label sits (a fraction `labelT` along the drawn path)
  polyline: Point[] | null; // the drawn polyline (straight/elbow) for crossing/line-jump math; null when curved
  samplePts: Point[]; // the drawn path sampled as a polyline (straight/elbow → exact; curved → sampled) —
  // used to place + drag the label ALONG the path, so it rides the segments as the connector reshapes.
}

const polyPath = (v: Point[]) => (v.length ? `M ${v[0].x} ${v[0].y}` + v.slice(1).map((p) => ` L ${p.x} ${p.y}`).join('') : '');

/** The border point on `box` facing `toward`, snapped to an edge midpoint, with that edge's outward normal. */
function sideAttach(box: Box, toward: Point): { point: Point; normal: Point } {
  const c = elementCenter(box);
  const dx = toward.x - c.x;
  const dy = toward.y - c.y;
  if (Math.abs(dx) >= Math.abs(dy)) {
    const right = dx >= 0;
    return { point: { x: right ? box.x + box.w : box.x, y: c.y }, normal: { x: right ? 1 : -1, y: 0 } };
  }
  const down = dy >= 0;
  return { point: { x: c.x, y: down ? box.y + box.h : box.y }, normal: { x: 0, y: down ? 1 : -1 } };
}

/** The attach point + outward normal for a PINNED anchor (a normalized perimeter point on the box). The
 *  outward normal is the nearest edge in NORMALIZED space — so it follows whichever edge the anchor sits
 *  on regardless of which coordinate is pinned (a corner anchor no longer forces a vertical normal). */
function anchorAttach(box: Box, a: { nx: number; ny: number }): { point: Point; normal: Point } {
  const nx = Math.max(0, Math.min(1, a.nx));
  const ny = Math.max(0, Math.min(1, a.ny));
  const point = { x: box.x + nx * box.w, y: box.y + ny * box.h };
  const dl = nx;
  const dr = 1 - nx;
  const dt = ny;
  const db = 1 - ny;
  const m = Math.min(dl, dr, dt, db);
  const normal: Point =
    m === dr ? { x: 1, y: 0 } : m === dl ? { x: -1, y: 0 } : m === db ? { x: 0, y: 1 } : { x: 0, y: -1 };
  return { point, normal };
}

/** Project a world point onto a box's PERIMETER as a normalized anchor {nx,ny}: snap to the nearest
 *  edge, then soft-snap the along-edge coordinate to that edge's centre (so dragging near the middle
 *  clicks to it). Used to pin — and slide — a connector endpoint along an element's edge.
 *
 *  Edge choice is by true distance to the edge SEGMENT (its closest point), not the infinite edge line —
 *  so a point dragged straight out past one edge stays on that edge instead of snapping to a
 *  perpendicular one once it clears the box's half-extent. */
export function perimeterAnchor(box: Box, p: Point): { nx: number; ny: number } {
  const c01 = (v: number) => Math.max(0, Math.min(1, v));
  const w = box.w || 1;
  const h = box.h || 1;
  const x0 = box.x;
  const x1 = box.x + box.w;
  const y0 = box.y;
  const y1 = box.y + box.h;
  const cx = Math.max(x0, Math.min(x1, p.x)); // p clamped onto the box in x (for the top/bottom segments)
  const cy = Math.max(y0, Math.min(y1, p.y)); // …and in y (for the left/right segments)
  const sq = (v: number) => v * v;
  const cand = [
    { nx: 0, ny: c01((cy - y0) / h), d: sq(p.x - x0) + sq(p.y - cy) }, // left
    { nx: 1, ny: c01((cy - y0) / h), d: sq(p.x - x1) + sq(p.y - cy) }, // right
    { nx: c01((cx - x0) / w), ny: 0, d: sq(p.x - cx) + sq(p.y - y0) }, // top
    { nx: c01((cx - x0) / w), ny: 1, d: sq(p.x - cx) + sq(p.y - y1) }, // bottom
  ];
  let best = cand[0];
  for (const c of cand) if (c.d < best.d) best = c;
  let { nx, ny } = best;
  const SNAP = 0.16; // soft-snap the free coordinate to the edge centre (0.5) within ~16% of the edge
  if (nx === 0 || nx === 1) {
    if (Math.abs(ny - 0.5) < SNAP) ny = 0.5;
  } else if (Math.abs(nx - 0.5) < SNAP) nx = 0.5;
  return { nx, ny };
}

/** A smooth Catmull-Rom spline (as cubic béziers) through the points. */
function catmullRom(pts: Point[]): string {
  if (pts.length < 2) return '';
  if (pts.length === 2) return polyPath(pts);
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${p2.x} ${p2.y}`;
  }
  return d;
}

/** Minimum perpendicular stub (world px) an elbow leaves/enters an edge with, so it never runs parallel
 *  along (hugging) the edge — the first and last segments always shoot straight out of the border. */
const ELBOW_STUB = 22;

/** Drop consecutive duplicate points and interior points that are collinear AND monotonic with their
 *  neighbours (redundant midpoints), while KEEPING reversal points — so a required perpendicular stub is
 *  never flattened into an inward segment. */
function simplifyPath(pts: Point[]): Point[] {
  const q: Point[] = [];
  for (const p of pts) {
    const last = q[q.length - 1];
    if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 0.01) q.push(p);
  }
  if (q.length <= 2) return q;
  const out: Point[] = [q[0]];
  for (let i = 1; i < q.length - 1; i++) {
    const a = out[out.length - 1];
    const b = q[i];
    const c = q[i + 1];
    const colX = Math.abs(a.x - b.x) < 0.01 && Math.abs(b.x - c.x) < 0.01;
    const colY = Math.abs(a.y - b.y) < 0.01 && Math.abs(b.y - c.y) < 0.01;
    const monotonic = colX ? (b.y - a.y) * (c.y - b.y) >= 0 : colY ? (b.x - a.x) * (c.x - b.x) >= 0 : false;
    if ((colX || colY) && monotonic) continue; // b is a redundant midpoint
    out.push(b);
  }
  out.push(q[q.length - 1]);
  return out;
}

/**
 * Orthogonal route between two endpoints that LEAVES each edge along its outward normal (perpendicular),
 * then connects the two stubs with axis-aligned segments — the Miro right-angle look. Returns the drawn
 * points plus the single draggable MIDDLE segment (if the route has one), whose position the user can pin
 * via `elbowMid` (moving the segment, not adding an anchor). `elbowMid` is AXIS-TAGGED — a horizontal rail
 * pins its y, a vertical rail its x — so a pin from one layout is ignored (auto) once the route reshapes
 * into the other orientation instead of jumping to a stale coordinate.
 *
 *  - Parallel normals with room: a Z-route — stub out, one middle rail (clamped to the OUTWARD side of
 *    both edges so a stub never inverts into its own box), stub in.
 *  - Parallel normals whose edges overlap (no room for a straight rail): an around-route that leaves
 *    perpendicular, clears the boxes on the nearer side, and comes back in perpendicular.
 *  - Perpendicular normals: an L via explicit stubs, so both ends stay perpendicular and the end-arrow
 *    direction is well-defined even when the two endpoints share a coordinate.
 */
function elbowAuto(
  from: Box,
  to: Box,
  start: Point,
  sn: Point,
  end: Point,
  en: Point,
  elbowMid?: { axis: 'x' | 'y'; v: number },
): { points: Point[]; rail: { mid: Point; orientation: 'railh' | 'railv' } | null } {
  const S = ELBOW_STUB;
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
  const mid = (p: Point, q: Point): Point => ({ x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 });
  const sVert = sn.y !== 0;
  const eVert = en.y !== 0;
  const s1 = { x: start.x + sn.x * S, y: start.y + sn.y * S }; // perpendicular stub end at start
  const e1 = { x: end.x + en.x * S, y: end.y + en.y * S }; // …and at end

  // Degenerate: the attach points coincide (edge-adjacent / identical boxes projecting to the same border
  // point) → a straight zero-length link, not an out-and-back stub spur.
  if (Math.hypot(start.x - end.x, start.y - end.y) < 0.5) return { points: [start, end], rail: null };

  if (sVert && eVert) {
    // Horizontal rail at railY. Valid range = railY on the OUTWARD side of each edge (the stub may be
    // short but never inverts): sn.y>0 needs railY ≥ start.y, sn.y<0 needs railY ≤ start.y (and the end
    // likewise). The around-route is used ONLY when this range is empty (the boxes truly overlap) — not
    // merely when they're closer than a stub, which is a perfectly good short Z.
    const lo = Math.max(sn.y > 0 ? start.y : -Infinity, en.y > 0 ? end.y : -Infinity);
    const hi = Math.min(sn.y < 0 ? start.y : Infinity, en.y < 0 ? end.y : Infinity);
    if (lo <= hi) {
      // Prefer a full-stub offset when there's room (clamped into the range); centre it when facing.
      const def = sn.y === en.y ? (sn.y > 0 ? Math.max(s1.y, e1.y) : Math.min(s1.y, e1.y)) : (start.y + end.y) / 2;
      const railY = clamp(elbowMid?.axis === 'y' ? elbowMid.v : def, lo, hi);
      const a = { x: start.x, y: railY };
      const b = { x: end.x, y: railY };
      const rail = Math.abs(a.x - b.x) > 0.5 ? { mid: mid(a, b), orientation: 'railh' as const } : null;
      return { points: simplifyPath([start, a, b, end]), rail };
    }
    // True overlap → route around the nearer side. The vertical rail is clamped to STAY OUTSIDE the union
    // box on that side (its exclusion zone is the interior), so a pin can't drag it back through the shapes.
    const leftX = Math.min(from.x, to.x) - S;
    const rightX = Math.max(from.x + from.w, to.x + to.w) + S;
    const c2 = (start.x + end.x) / 2;
    const preferLeft = c2 - leftX <= rightX - c2;
    const pin = elbowMid?.axis === 'x' ? elbowMid.v : undefined;
    const railX = preferLeft ? Math.min(pin ?? leftX, leftX) : Math.max(pin ?? rightX, rightX);
    const b = { x: railX, y: s1.y };
    const c = { x: railX, y: e1.y };
    return { points: simplifyPath([start, { x: start.x, y: s1.y }, b, c, { x: end.x, y: e1.y }, end]), rail: { mid: mid(b, c), orientation: 'railv' } };
  }

  if (!sVert && !eVert) {
    // Vertical rail at railX (mirror of the above).
    const lo = Math.max(sn.x > 0 ? start.x : -Infinity, en.x > 0 ? end.x : -Infinity);
    const hi = Math.min(sn.x < 0 ? start.x : Infinity, en.x < 0 ? end.x : Infinity);
    if (lo <= hi) {
      const def = sn.x === en.x ? (sn.x > 0 ? Math.max(s1.x, e1.x) : Math.min(s1.x, e1.x)) : (start.x + end.x) / 2;
      const railX = clamp(elbowMid?.axis === 'x' ? elbowMid.v : def, lo, hi);
      const a = { x: railX, y: start.y };
      const b = { x: railX, y: end.y };
      const rail = Math.abs(a.y - b.y) > 0.5 ? { mid: mid(a, b), orientation: 'railv' as const } : null;
      return { points: simplifyPath([start, a, b, end]), rail };
    }
    const topY = Math.min(from.y, to.y) - S;
    const botY = Math.max(from.y + from.h, to.y + to.h) + S;
    const c2 = (start.y + end.y) / 2;
    const preferTop = c2 - topY <= botY - c2;
    const pin = elbowMid?.axis === 'y' ? elbowMid.v : undefined;
    const railY = preferTop ? Math.min(pin ?? topY, topY) : Math.max(pin ?? botY, botY);
    const b = { x: s1.x, y: railY };
    const c = { x: e1.x, y: railY };
    return { points: simplifyPath([start, { x: s1.x, y: start.y }, b, c, { x: e1.x, y: end.y }, end]), rail: { mid: mid(b, c), orientation: 'railh' } };
  }

  // Perpendicular normals → an L via explicit stubs.
  const corner = sVert ? { x: s1.x, y: e1.y } : { x: e1.x, y: s1.y };
  return { points: simplifyPath([start, s1, corner, e1, end]), rail: null };
}

/** Sample a cubic Bézier into `n` points (inclusive of both ends) for label placement along a curve. */
function sampleCubic(p0: Point, c1: Point, c2: Point, p3: Point, n: number): Point[] {
  const out: Point[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const u = 1 - t;
    const a = u * u * u;
    const b = 3 * u * u * t;
    const c = 3 * u * t * t;
    const dd = t * t * t;
    out.push({ x: a * p0.x + b * c1.x + c * c2.x + dd * p3.x, y: a * p0.y + b * c1.y + c * c2.y + dd * p3.y });
  }
  return out;
}

/** Point a fraction `t` (0..1) of the way along a polyline by ARC LENGTH. */
export function pointAlong(pts: Point[], t: number): Point {
  if (pts.length === 0) return { x: 0, y: 0 };
  if (pts.length === 1) return pts[0];
  const segLen: number[] = [];
  let total = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const l = Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
    segLen.push(l);
    total += l;
  }
  if (total === 0) return pts[0];
  let target = Math.max(0, Math.min(1, t)) * total;
  for (let i = 0; i < segLen.length; i++) {
    if (target <= segLen[i] || i === segLen.length - 1) {
      const f = segLen[i] ? target / segLen[i] : 0;
      return { x: pts[i].x + (pts[i + 1].x - pts[i].x) * f, y: pts[i].y + (pts[i + 1].y - pts[i].y) * f };
    }
    target -= segLen[i];
  }
  return pts[pts.length - 1];
}

/** The arc-length fraction (0..1) of the point on a polyline closest to `p` — for dragging a label
 *  along the path (project the cursor onto the drawn segments). */
export function projectToPolylineT(pts: Point[], p: Point): number {
  if (pts.length < 2) return 0.5;
  const segLen: number[] = [];
  let total = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const l = Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
    segLen.push(l);
    total += l;
  }
  if (total === 0) return 0.5;
  let best = { d2: Infinity, len: 0 };
  let acc = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const ax = pts[i].x;
    const ay = pts[i].y;
    const dx = pts[i + 1].x - ax;
    const dy = pts[i + 1].y - ay;
    const len2 = dx * dx + dy * dy;
    let u = len2 ? ((p.x - ax) * dx + (p.y - ay) * dy) / len2 : 0;
    u = u < 0 ? 0 : u > 1 ? 1 : u;
    const cx = ax + u * dx;
    const cy = ay + u * dy;
    const d2 = (p.x - cx) * (p.x - cx) + (p.y - cy) * (p.y - cy);
    if (d2 < best.d2) best = { d2, len: acc + u * segLen[i] };
    acc += segLen[i];
  }
  return best.len / total;
}

/**
 * Full geometry for a connector between two boxes, honouring its routing type (straight / elbow /
 * curved) and any user waypoints. Endpoints attach to the box borders; elbow/curved leave the edge
 * perpendicular (like Miro). Returns the SVG path plus the control points needed for the drag handles.
 */
export function connectorGeometry(
  from: Box,
  to: Box,
  style: {
    connectorType?: string;
    waypoints?: { x: number; y: number }[];
    from_anchor?: { nx: number; ny: number };
    to_anchor?: { nx: number; ny: number };
    elbowMid?: { axis: 'x' | 'y'; v: number };
    labelT?: number;
  },
): ConnectorGeom {
  const type = style.connectorType ?? 'curved';
  const wp = style.waypoints ?? [];
  const fc = elementCenter(from);
  const tc = elementCenter(to);

  const mid = (p: Point, q: Point): Point => ({ x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 });
  let start: Point;
  let end: Point;
  let startFrom: Point;
  let endFrom: Point;
  let d: string;
  let handles: ConnectorHandle[] = [];
  let polyline: Point[] | null = null;
  let samplePts: Point[];

  if (type === 'straight') {
    // A pinned anchor keeps the endpoint at its chosen perimeter spot; else exit toward the other end.
    start = style.from_anchor ? anchorAttach(from, style.from_anchor).point : rayBoxExit(from, fc, wp[0] ?? tc);
    end = style.to_anchor ? anchorAttach(to, style.to_anchor).point : rayBoxExit(to, tc, wp[wp.length - 1] ?? fc);
    const v = [start, ...wp, end];
    d = polyPath(v);
    startFrom = v[1];
    endFrom = v[v.length - 2];
    polyline = v;
    samplePts = v;
    // Straight: a SINGLE midpoint handle dragging the one bend (moves it; no new points each drag).
    handles = wp.length === 0 ? [{ mid: mid(start, end), controlIndex: 0, orientation: 'free' }] : [];
  } else {
    // Elbow faces the OTHER box's centre (it auto-routes, ignoring waypoints); curved may face its first/
    // last waypoint. A pinned anchor overrides facing with the fixed perimeter spot + that edge's normal.
    const towardFrom = type === 'elbow' ? tc : wp[0] ?? tc;
    const towardTo = type === 'elbow' ? fc : wp[wp.length - 1] ?? fc;
    const a = style.from_anchor ? anchorAttach(from, style.from_anchor) : sideAttach(from, towardFrom);
    const b = style.to_anchor ? anchorAttach(to, style.to_anchor) : sideAttach(to, towardTo);
    start = a.point;
    end = b.point;
    if (type === 'elbow') {
      // Always auto-route orthogonally with PERPENDICULAR stubs (never hugging the edge); the single
      // middle segment is draggable, moving the rail via `elbowMid` (no anchor point added per drag).
      // Legacy `waypoints` are intentionally ignored (kept in the data) — the old per-drag splice model,
      // which spawned an anchor on every segment drag, is gone.
      const { points, rail } = elbowAuto(from, to, start, a.normal, end, b.normal, style.elbowMid);
      d = polyPath(points);
      startFrom = points[1];
      endFrom = points[points.length - 2];
      polyline = points;
      samplePts = points;
      if (rail) handles = [{ mid: rail.mid, controlIndex: 0, orientation: rail.orientation }];
    } else {
      // curved
      if (wp.length === 0) {
        const k = Math.max(40, Math.min(220, Math.hypot(end.x - start.x, end.y - start.y) / 2));
        const c1 = { x: start.x + a.normal.x * k, y: start.y + a.normal.y * k };
        const c2 = { x: end.x + b.normal.x * k, y: end.y + b.normal.y * k };
        d = `M ${start.x} ${start.y} C ${c1.x} ${c1.y} ${c2.x} ${c2.y} ${end.x} ${end.y}`;
        startFrom = c1;
        endFrom = c2;
        samplePts = sampleCubic(start, c1, c2, end, 32);
        // The single bend handle sits on the drawn curve (cubic t=0.5), not the straight chord.
        handles = [
          { mid: { x: (start.x + 3 * c1.x + 3 * c2.x + end.x) / 8, y: (start.y + 3 * c1.y + 3 * c2.y + end.y) / 8 }, controlIndex: 0, orientation: 'free' },
        ];
      } else {
        const v = [start, ...wp, end];
        d = catmullRom(v);
        startFrom = v[1];
        endFrom = v[v.length - 2];
        samplePts = v; // rough sample (control polyline) — good enough to place a label near a wavy curve
      }
    }
  }

  // The label rides a fraction `labelT` (arc length) along the DRAWN path, so it stays on the segments as
  // the connector reshapes; default is the middle (0.5).
  const labelAnchor = pointAlong(samplePts, style.labelT ?? 0.5);
  // Elbow auto-routes and ignores waypoints, so it exposes NO waypoint dots (only its rail handle) — the
  // ghost-dot bug. Straight/curved still surface their bend point(s).
  return { d, start, end, startFrom, endFrom, waypoints: type === 'elbow' ? [] : wp, handles, labelAnchor, polyline, samplePts };
}

/** Intersection point of segments p1p2 and p3p4, or null if they don't cross within both segments. */
export function segIntersect(p1: Point, p2: Point, p3: Point, p4: Point): Point | null {
  const denom = (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y);
  if (denom === 0) return null;
  const ua = ((p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x)) / denom;
  const ub = ((p2.x - p1.x) * (p1.y - p3.y) - (p2.y - p1.y) * (p1.x - p3.x)) / denom;
  if (ua <= 0.02 || ua >= 0.98 || ub <= 0.02 || ub >= 0.98) return null; // ignore crossings at the very ends
  return { x: p1.x + ua * (p2.x - p1.x), y: p1.y + ua * (p2.y - p1.y) };
}

/** An SVG polyline path that "hops" (small semicircle) over each crossing point (Miro line-jumps). */
export function jumpedPolyPath(points: Point[], jumps: { i: number; point: Point }[], r: number): string {
  if (points.length < 2) return '';
  let d = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const ux = (b.x - a.x) / len;
    const uy = (b.y - a.y) / len;
    const segJumps = jumps
      .filter((j) => j.i === i)
      .map((j) => ({ t: (j.point.x - a.x) * ux + (j.point.y - a.y) * uy }))
      .filter((j) => j.t > r && j.t < len - r)
      .sort((x, y) => x.t - y.t);
    for (const j of segJumps) {
      const px = a.x + ux * (j.t - r);
      const py = a.y + uy * (j.t - r);
      const qx = a.x + ux * (j.t + r);
      const qy = a.y + uy * (j.t + r);
      d += ` L ${px.toFixed(1)} ${py.toFixed(1)} A ${r} ${r} 0 0 1 ${qx.toFixed(1)} ${qy.toFixed(1)}`;
    }
    d += ` L ${b.x.toFixed(1)} ${b.y.toFixed(1)}`;
  }
  return d;
}

/** Bounding box (with a little padding) of a flat point list, in world coords. */
export function pointsBounds(worldPoints: number[]): { x: number; y: number; w: number; h: number } {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < worldPoints.length; i += 2) {
    minX = Math.min(minX, worldPoints[i]);
    maxX = Math.max(maxX, worldPoints[i]);
    minY = Math.min(minY, worldPoints[i + 1]);
    maxY = Math.max(maxY, worldPoints[i + 1]);
  }
  const pad = 2;
  return { x: minX - pad, y: minY - pad, w: maxX - minX + 2 * pad, h: maxY - minY + 2 * pad };
}
